// What the Billing screen says about what Naurist sells — everything about it
// that is not a price.
//
// The prices used to be here too, as a list the app shipped with, because there
// was no endpoint behind them. There is one now: `GET /api/v1/billing/plans/`,
// read by `plansService`, and it is the only place a figure comes from. Nothing
// in this feature writes one down any more — a price list in a bundle is a
// price somebody is still being quoted from cache the day it changes.
//
// What is left is copy. The feature list is ours to write and the backend has
// no opinion on it; the product name and the cycle labels are how this app says
// out loud what the plan rows mean.

import type { BillingCycleId, PlanFeature } from "../types/billing.types";

/**
 * The small print under a term's price: a line, and the coupon code it is
 * asking for where there is one.
 *
 * The code is split out rather than written into the sentence because the row
 * puts a copy button beside it — a code somebody has to retype from a line of
 * prose is a code typed wrong.
 */
export interface CyclePriceNote {
  note: string;
  code?: string;
}

/** One quote on the Billings hero. */
export interface Testimonial {
  quote: string;
  /** Who said it. */
  name: string;
  /** Where they practise — "JP Nagar, Bengaluru". */
  location: string;
}

/**
 * What the subscription includes. One list, shared by both cards: there is a
 * single product and the cycles differ only in what they cost, which is what
 * "One plan. Complete access" above it is saying.
 */
export const PRO_FEATURES: PlanFeature[] = [
  {
    name: "Client management",
    description:
      "Keep client profiles, consultation details, and payment records organised.",
  },
  {
    name: "Personalised meal planning",
    description:
      "Create customised meal plans powered by accurate nutrition data.",
  },
  {
    name: "Dish & nutrition database",
    description:
      "Access a growing database of dishes, ingredients, and nutrient values.",
  },
  {
    name: "Branded Documents",
    description: "Share meal plans and invoices with your own logo and branding.",
  },
  {
    name: "Live Support",
    description: "Get help whenever you need it from our support team.",
  },
];

/**
 * What the product is called, for the screens that have to name it without a
 * plan in hand.
 *
 * The cards take the name off the plan the endpoint sent — that is the seller's
 * own word for what is on sale. This is for the plan *details* screen, which
 * names a subscription somebody already has: what they are paying for should
 * not go unnamed because a price list they are not being shown failed to load.
 */
export const PRODUCT_NAME = "Naurist Pro";

/** The cycle, as every screen writes it. Ours rather than the backend's, which
 *  says `MONTH` — a word for a machine, not for a card. */
export const CYCLE_LABELS: Record<BillingCycleId, string> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  "half-yearly": "Half-yearly",
  yearly: "Yearly",
};

/**
 * The same thing said for a term this app sells no card for.
 *
 * `CYCLE_LABELS` above is keyed by the two cycles the *price list* offers, and
 * that is the right key for a card somebody is being asked to buy. It is the
 * wrong key for a subscription somebody already has: the backend issues terms
 * this app never put on sale — a week of internal access, whatever is granted
 * next — and reading one of those through a two-valued map does not fail, it
 * answers "Monthly", which is a plain lie about what is being charged.
 *
 * So the pill on the plan-details card is keyed by the backend's own interval
 * word instead, and this is only the translation from its vocabulary into
 * ours. A word that is not in here is title-cased and shown as it came rather
 * than rounded to the nearest term we recognise; see `labelOfInterval`.
 */
export const INTERVAL_LABELS: Record<string, string> = {
  DAY: "Daily",
  WEEK: "Weekly",
  MONTH: "Monthly",
  QUARTER: "Quarterly",
  HALF_YEAR: "Half-yearly",
  YEAR: "Yearly",
};

/**
 * The line of small print under a card's price, for the cycle that has one.
 *
 * The year is sold at the launch price *and* a further cut for committing to a
 * year, and the struck-through figure beside the price cannot say which of the
 * two is which — it is one discount by the time it reaches the card. This says
 * the second one out loud, on the card it applies to.
 *
 * Copy, like everything else in this file, and keyed by cycle rather than by
 * which card is being recommended: it is a claim about the yearly plan, not
 * about the layout. That does mean the figure in the sentence is written here
 * while the prices come off `GET /api/v1/billing/plans/` — the endpoint sends
 * one discount per term and not its parts, so there is nothing to derive it
 * from. If the yearly cut is ever re-priced away from 10%, this line has to
 * follow it.
 */
export const CYCLE_PRICE_NOTES: Partial<Record<BillingCycleId, CyclePriceNote>> =
  {
    yearly: {
      note: "Additional 10% off - Use",
      code: "YEARLY10",
    },
  };

/**
 * What the Billings tab's hero rotates through, one at a time.
 *
 * Copy, like everything else in this file, and said by people rather than by
 * us — which is the whole of what it is for. There is no endpoint behind it and
 * there should not be one: these are quotes that were given to us for the
 * marketing panel, not rows in anybody's database, and they change when the
 * copy changes.
 *
 * Four of them, and the panel moves on every few seconds by itself; see
 * `PlanShowcase`. There is no way to pick one — it pauses while the pointer
 * rests on it, and that is the whole of the control.
 */
export const TESTIMONIALS: Testimonial[] = [
  {
    quote:
      "Naurist has made my day-to-day work much easier. Creating meal plans, managing clients, and sharing everything professionally now takes much less time, so I can focus more on my clients.",
    name: "Megha Vohra",
    location: "JP Nagar, Bengaluru",
  },
  {
    quote:
      "I used to keep consultations in one place, plans in another and payments in a notebook. Having all three in one screen is the thing I did not know I was missing.",
    name: "Ritika Sharma",
    location: "Andheri West, Mumbai",
  },
  {
    quote:
      "My clients notice the difference. The plans go out with my own logo on them, which makes a small practice look like the practice I want it to be.",
    name: "Ananya Iyer",
    location: "Alwarpet, Chennai",
  },
  {
    quote:
      "The dish database saves me the part of the job I liked least. I build a week of meals in the time it used to take me to look up the numbers for one day.",
    name: "Priya Nair",
    location: "Vashi, Navi Mumbai",
  },
];

/**
 * How long a refund can be asked for, as the bar under the cards says it.
 *
 * "Money-back guarantee" rather than "refund policy": the same seven days
 * either way, said as the promise it is rather than as the name of a document.
 */
export const REFUND_POLICY = "7-Day money-back guarantee";

/**
 * The seven days, as a number, for the copy that has to name them.
 *
 * Copy and nothing else. The days used to be counted here too — the browser
 * worked out from the invoice list and its own clock whether the window was
 * still open — and that is `GET /api/v1/billing/refund/`'s answer now: it says
 * whether the refund can be had and the moment it lapses, and the screen draws
 * what it is told. This is only how the promise is worded, which is why it can
 * safely live in the bundle: the number in a sentence is a policy, and the
 * decision it used to drive is no longer taken here.
 */
export const REFUND_WINDOW_DAYS = 7;

/**
 * Which row wears "MOST SUBSCRIBED": the first of these the price list actually
 * offers.
 *
 * Stated rather than worked out — this is a decision about how the page reads
 * and not a claim about the figures, which is exactly how the price list has
 * always had it. Deriving it from whichever term saves the most would put the
 * design at the mercy of a price change: a year priced level with the month for
 * a week would quietly leave the page with nothing recommended, which is not a
 * layout anybody drew.
 *
 * An order rather than a single term because the list is no longer a fixed
 * pair. The quarter leads it, which is the badge the design puts there, and
 * today — with only a month and a year on the wire — it falls through to the
 * year, exactly where the badge is now. Whichever row it lands on, there is
 * always one and never two.
 *
 * The saving badge is the opposite case and is worked out in `plansService`,
 * because that one *is* a claim about the figures.
 *
 * It lives here, with the rest of the copy, rather than beside either price
 * list: both services draw the same duration rows off different endpoints, and
 * where the badge goes is one decision about how the page reads. Two copies of
 * it would be two pages that could disagree.
 */
const RECOMMENDED_CYCLES: BillingCycleId[] = [
  "quarterly",
  "half-yearly",
  "yearly",
  "monthly",
];

/**
 * Which of the terms actually on sale gets the badge — the first of
 * `RECOMMENDED_CYCLES` that is among them, or `null` for a list with none of
 * them in it, which cannot happen while those four are also the only terms
 * either price list can produce.
 */
export const recommendedOf = (
  cycles: BillingCycleId[]
): BillingCycleId | null =>
  RECOMMENDED_CYCLES.find((candidate) => cycles.includes(candidate)) ?? null;
